import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";

import { ISurveyBuilderQuestion } from "../../../../../../../@types/SurveyBuilder";
import { sanitizeHTML } from "../../../../../../../utils/htmlSanitizer";
import { getSurveyOptionLabel } from "../../../../../../../utils/surveyOptionLabels";
import { Chip, Chips, MutedText } from "../../../../SurveyBuilder.styles";
import {
  InfoText,
  OptionList,
  OptionRow,
  OptionValue,
  QuestionText,
  SummaryContainer,
} from "./QuestionSummary.styles";

interface IQuestionSummaryProps {
  question: ISurveyBuilderQuestion;
  /** "Question #n" for a question id, to name jump targets. */
  questionLabel(questionId: string): string | undefined;
}

/** EN-5261: a question card's compact, left-aligned summary (the Preview tab shows the patient view). */
export function QuestionSummary({
  question,
  questionLabel,
}: IQuestionSummaryProps) {
  const { t } = useTranslation();
  const isInfo = question.type === "Informative";

  return (
    <SummaryContainer>
      {isInfo ? (
        // Info pages hold the rich text editor's HTML; keep its line breaks and formatting.
        <InfoText>
          {ReactHtmlParser(sanitizeHTML(question.text ?? ""))}
        </InfoText>
      ) : (
        <QuestionText>{question.text}</QuestionText>
      )}
      {question.description && <MutedText>{question.description}</MutedText>}

      {question.options.length > 0 && question.type !== "SliderVas" && (
        <OptionList>
          {question.options.map((option) => {
            const target = option.nextQuestionId
              ? questionLabel(option.nextQuestionId)
              : undefined;
            return (
              <OptionRow key={option.id ?? option.label}>
                <span>{option.label}</span>
                {target && (
                  <OptionValue>
                    {t("surveys_builder_content_jumpsTo", { question: target })}
                  </OptionValue>
                )}
              </OptionRow>
            );
          })}
        </OptionList>
      )}
    </SummaryContainer>
  );
}

/** The question's type and settings, shown beside its card title. */
export function QuestionTags({
  question,
  questionLabel,
}: IQuestionSummaryProps) {
  const { t } = useTranslation();
  const isInfo = question.type === "Informative";
  const jumpTo = question.nextQuestionId
    ? questionLabel(question.nextQuestionId)
    : undefined;

  return (
    <Chips>
      <Chip>{getSurveyOptionLabel("questionType", question.type, t)}</Chip>
      {question.mandatory && !isInfo && (
        <Chip>{t("surveys_builder_question_mandatory")}</Chip>
      )}
      {question.domain && <Chip>{question.domain}</Chip>}
      {jumpTo && (
        <Chip>
          {t("surveys_builder_content_jumpsTo", { question: jumpTo })}
        </Chip>
      )}
    </Chips>
  );
}
